import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Image,
  Box,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import axios from "axios";
import { FiUpload } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import {
  REQUEST_CREATE_MASTER_DISHES,
  REQUEST_MASTER_DISHES,
} from "../../store/dishes/dishesActionTypes";

interface DishesEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDishAdded?: () => void;
  dishData?: any;
}

const DishesEditModal: React.FC<DishesEditModalProps> = ({
  isOpen,
  onClose,
  onDishAdded,
  dishData,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);

  useEffect(() => {
    // Prefill image preview and ID if dishData exists
    if (dishData?.image?.url) {
      setImagePreview(
        `${process.env.REACT_APP_IMAGE_BASE_URL}/${dishData.image.url}`
      );
      setImageId(dishData.image._id);
    }
  }, [dishData]);

  const initialValues = useMemo(() => {
    if (dishData) {
      return {
        name: dishData?.name || "",
      };
    }
    return {
      name: "",
    };
  }, [dishData]);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append("file", file);

      setIsImageUploading(true);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/upload/media`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        setImageId(response.data.data._id);
        toast({
          title: "Image Uploaded",
          description: "Dish category image uploaded successfully",
          status: "success",
        });
      } catch (error) {
        toast({
          title: "Image Upload Failed",
          description: "Could not upload dish category image",
          status: "error",
        });
        setImageId(null);
        setImagePreview(null);
      } finally {
        setIsImageUploading(false);
      }
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (
    values: { name: string },
    { setSubmitting }: any
  ) => {
    try {
      dispatch({
        type: REQUEST_CREATE_MASTER_DISHES,
        payload: {
          name: values.name,
          image: imageId,
        },
        onSuccess: () => {
          toast({
            title: "Dish Category Added",
            description: "New dish category created successfully",
            status: "success",
          });
          dispatch({ type: REQUEST_MASTER_DISHES });
          onDishAdded?.();
          onClose();
        },
        onError: (error: any) => {
          toast({
            title: "Error",
            description: error.message || "Failed to create dish category",
            status: "error",
          });
        },
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add dish category",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };
  // const previousRestaurants = useMemo(() => {
  //   return masterRestaurant;
  // }, []);

  // useEffect(() => {
  //   if (restaurantData) {
  //     const restaurantFromStore = masterRestaurant.find(
  //       (r: any) => r._id === restaurantData._id
  //     );

  //     if (restaurantFromStore !== restaurantData) {
  //       toast({
  //         title: "Success",
  //         description: "Restaurant has been updated",
  //         status: "success",
  //         isClosable: true,
  //         position: "top-right",
  //       });
  //       onClose();
  //     }
  //   } else {
  //     if (masterRestaurant.length !== previousRestaurants.length) {
  //       toast({
  //         title: "Success",
  //         description: "New restaurant has been created",
  //         status: "success",
  //         isClosable: true,
  //         position: "top-right",
  //       });
  //       onClose();
  //     }
  //   }
  // }, [masterRestaurant, previousRestaurants, restaurantData, onClose, toast]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{dishData ? "Edit" : "Add"} Dish Category</ModalHeader>
        <ModalCloseButton />
        <Formik
          initialValues={initialValues}
          validationSchema={Yup.object().shape({
            name: Yup.string()
              .required("Dish name is required")
              .min(2, "Dish name must be at least 2 characters"),
          })}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              <ModalBody>
                <FormControl  mb={4}>
                  <FormLabel>Dish Category Name</FormLabel>
                  <Field
                    as={Input}
                    name="name"
                    placeholder="Enter dish category name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Dish Category Image</FormLabel>
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    display="none"
                  />
                  <Button
                    leftIcon={<FiUpload />}
                    onClick={() => fileInputRef.current?.click()}
                    variant="ghost"
                    color="#E24441"
                    border="1px dashed #E24441"
                    p={4}
                    borderRadius="md"
                    width="full"
                  >
                    Upload Dish Image
                  </Button>

                  {imagePreview && (
                    <Box
                      mt={2}
                      position="relative"
                      width="100px"
                      height="100px"
                    >
                      <Image
                        src={imagePreview}
                        alt="Dish Category Preview"
                        boxSize="100px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                      <IconButton
                        aria-label="Remove image"
                        icon={<RiDeleteBin6Line />}
                        color="#E24441"
                        size="sm"
                        position="absolute"
                        top={-1}
                        right={-1}
                        background="white"
                        onClick={removeImage}
                      />
                    </Box>
                  )}
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Button
                  colorScheme="red"
                  type="submit"
                  isLoading={isSubmitting || isImageUploading}
                  isDisabled={!imageId}
                >
                  {dishData ? "Update" : "Add"} Dish
                </Button>
                <Button variant="ghost" onClick={onClose} ml={3}>
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  );
};

export default DishesEditModal;
